export class DummyData {
  /**
   * Check if a response contains any dummy keywords
   * @param {string} response - The response to check
   * @param {Set<string>} commonKeywords - Set of common dummy keywords
   * @param {Set<string>} [supplementalDictionarySet] - Optional set of additional dummy keywords
   * @returns {boolean}
   */
  static dummyResponse(response, commonKeywords, supplementalDictionarySet = null) {
    if (!response) return false;

    const words = response.toString().trim().toLowerCase().split(/\s+/);
    return words.some(
      (word) => commonKeywords.has(word) || (supplementalDictionarySet && supplementalDictionarySet.has(word)),
    );
  }

  /**
   * Check if a phone number matches any dummy patterns
   * @param {string} phoneNumber - The phone number to check
   * @param {string[]} phonePatterns - Array of regex patterns for dummy phone numbers
   * @returns {boolean}
   */
  static dummyPhone(phoneNumber, phonePatterns) {
    if (!phoneNumber) return false;

    // Format phone number to match expected pattern
    const formattedNumber = phoneNumber
      .toString()
      .replace(/[^\d]/g, "") // Remove all non-digits
      .replace(/^(\d{10})$/, "+1$1"); // Add +1 prefix if it's a 10-digit number

    return phonePatterns.some((pattern) => new RegExp(pattern).test(formattedNumber));
  }

  /**
   * Check if an email address has a dummy username
   * @param {string} email - The email address to check
   * @param {Set<string>} dummyEmailUsernames - Set of dummy email usernames
   * @returns {boolean}
   */
  static dummyEmail(email, dummyEmailUsernames) {
    if (!email) return false;

    const username = email.toString().split("@")[0]?.toLowerCase();
    if (!username) return false;

    return dummyEmailUsernames.has(username);
  }

  /**
   * Check if the provided email is the same as the applicant's email
   * @param {string} email - The email address to check
   * @param {string} applicantEmail - The applicant's email address
   * @returns {boolean}
   */
  static sameEmailAsApplicant(email, applicantEmail) {
    if (!email || !applicantEmail) return false;

    return email.toString().toLowerCase().trim() === applicantEmail.toString().toLowerCase().trim();
  }

  /**
   * Check if the provided address matches any existing rent verification addresses
   * @param {Object} addressData - Object containing address fields (street1, street2, city, state, zip)
   * @param {Array<Object>} existingAddresses - Array of existing rent verification address objects
   * @returns {boolean}
   */
  static duplicateAddress(addressData, existingAddresses) {
    if (!addressData || !existingAddresses || existingAddresses.length === 0) return false;

    // Normalize the current address for comparison
    const normalizeAddress = (addr) => {
      const street1 = (addr.street1 || "").toString().toLowerCase().trim();
      const street2 = (addr.street2 || "").toString().toLowerCase().trim();
      const city = (addr.city || "").toString().toLowerCase().trim();
      const state = (addr.state || "").toString().toLowerCase().trim();
      const zip = (addr.zip || "").toString().toLowerCase().trim();

      return { street1, street2, city, state, zip };
    };

    const currentAddress = normalizeAddress(addressData);

    // Check if current address matches any existing addresses
    return existingAddresses.some((existingAddr) => {
      const existing = normalizeAddress(existingAddr);

      // Match on street1, street2, city, state, and zip
      // All fields must match for it to be considered a duplicate
      return (
        currentAddress.street1 === existing.street1 &&
        currentAddress.street2 === existing.street2 &&
        currentAddress.city === existing.city &&
        currentAddress.state === existing.state &&
        currentAddress.zip === existing.zip
      );
    });
  }
}
